import "../styles/default.scss";

import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import { compose } from "redux";

import commentExlamationIcon from "../../../../../assets/images/comment-exclamation.svg";
import withAttention from "../../../../../hocs/withAttention";
import withFirstAnalysis from "../../../../../hocs/withFirstAnalysis";
import withQuoteData from "../../../../../hocs/withQuoteData";
import getStockMessages from "../../../../../lib/getStockMessages";
import { shouldShowAdminComment } from "../../../../../lib/shouldShowAdminComment";
import { updateAttentionStateLocal } from "../../../../../lib/updateAttentionStateLocal";
import StockPageTags from "../../StockPageTags";
import withUserData from "../../../../../hocs/withUserData";
import Ticker from "../components/Ticker";
import LandingTypography from "../../../landingPage/pages/Homepage/components/LandingTypography";
import StockName from "../components/StockName";
import StockChange from "../components/StockChange/StockChange";
import StockAlertButton from "../components/StockAlertButton/StockAlertButton";
import StockAnalysisButton from "../components/StockAnalysisButton/StockAnalysisButton";
import useIsDesktop from "../../../landingPage/hooks/useIsDesktop";
import StockPageTagsButton from "../components/StockPageTagsButton/StockPageTagsButton";

const StockPageHeader = ({
  stock,
  translate,
  quoteData,
  title,
  attentionState,
  updateAttentionState,
  tags,
  stockId,
  firstAnalysis,
  stockData,
  firstAnalysisLoading
}) => {
  const isDesktop = useIsDesktop();
  const { attention_updated } = stock;
  const hasAttention = getStockMessages(stockData, translate).length;
  const showAdminComment = shouldShowAdminComment(
    attentionState,
    attention_updated
  );

  const { market, ticker, currency, name } = stock;
  const analysis = firstAnalysis?.[0];

  return (
    <LandingTypography className="stock-page-header">
      <div className="header-stock-data-section">
        <div className="additional-details">
          <h1 className="full-name">
            {title ? title : `${stock.name} (${stock.ticker})`}
            {!showAdminComment && hasAttention ? (
              <span
                className={"admin-comment-icon"}
                onClick={() =>
                  updateAttentionStateLocal(
                    market,
                    ticker,
                    updateAttentionState,
                    attentionState
                  )
                }
              >
                <img
                  alt={"Comment exclamation icon"}
                  src={commentExlamationIcon}
                />
              </span>
            ) : null}
          </h1>
        </div>
        <div
          className={classNames("flex", {
            "mt-5": isDesktop,
            "mx-2.5": !isDesktop
          })}
        >
          <div className={"flex gap-1 flex-col flex-1"}>
            <div className={"flex gap-6"}>
              {ticker && market ? (
                <Ticker ticker={ticker} market={market} />
              ) : null}
              {stockId ? (
                <StockPageTags
                  tags={tags || []}
                  stockId={stockId}
                  ticker={ticker}
                  showMarginBottom={false}
                  showPadding={false}
                />
              ) : null}
            </div>
            {name ? <StockName name={name} /> : null}
          </div>
          {quoteData?.change &&
          quoteData?.close &&
          quoteData?.datetime_utc ? (
            <StockChange
              change={quoteData.change}
              changeValue={quoteData.change_value}
              close={quoteData.close}
              currency={currency}
              latestQuoteDatetimeUtc={quoteData.datetime_utc}
            />
          ) : null}
        </div>
        <div
          className={classNames(
            "flex gap-4 justify-between align-center mt-4",
            { "mx-2.5": !isDesktop }
          )}
        >
          <div className={"flex gap-3"}>
            {stockId && ticker && market ? (
              <StockAlertButton
                stockId={stockId}
                ticker={ticker}
                market={market}
              />
            ) : null}
            {!isDesktop ? (
              <StockPageTagsButton stockId={stockId} ticker={ticker} />
            ) : null}
          </div>
          <div className={"max-w-2xl.3 flex-1"}>
            {!firstAnalysisLoading &&
            analysis?.id &&
            analysis?.publication_date ? (
              <StockAnalysisButton
                analysisId={analysis.id}
                analysisDate={analysis.publication_date}
                permissions={analysis.permissions}
              />
            ) : null}
          </div>
        </div>
      </div>
    </LandingTypography>
  );
};

StockPageHeader.propTypes = {
  stock: PropTypes.shape({
    ticker: PropTypes.string,
    currency: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.number
  }).isRequired,
  user_stock: PropTypes.object,
  userHasAccess: PropTypes.bool,
  canDisplaySection: PropTypes.func,
  signal: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  quote: PropTypes.object,
  firstAnalysis: PropTypes.array,
  firstAnalysisLoading: PropTypes.bool
};

export default compose(
  withQuoteData(),
  withAttention,
  withFirstAnalysis,
  withUserData
)(StockPageHeader);
